import { useState } from "react";

import { registry as registryApi } from "../api/resources";
import { RegistryTable } from "../components/RegistryTable";
import { Card, EmptyState, Loading, PageHeader, StatusBadge, Tabs } from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { displayValue } from "../lib/format";
import { useAsync } from "../lib/useAsync";

export function WorkflowsPage() {
  const { organizationId } = useCurrentOrganization();
  const [tab, setTab] = useState<"workflows" | "steps" | "manual" | "opportunities">("workflows");

  const steps = useAsync(
    () =>
      organizationId && tab === "steps"
        ? registryApi.list(organizationId, "workflow-steps", { limit: 200 })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId, tab],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Workflow registry"
        description="Actor → System → Input → Action → Output → Next actor. A list and detail view; process visualization is a later phase."
      />

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "workflows", label: "Workflows" },
          { value: "steps", label: "Steps" },
          { value: "manual", label: "Manual tasks" },
          { value: "opportunities", label: "Automation opportunities" },
        ]}
      />

      {tab === "workflows" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="workflows"
          emptyTitle="No workflows recorded"
          emptyDescription="Employee interviews produce the raw material for workflows; record the consolidated process here."
          columns={[
            { key: "name", label: "Workflow" },
            { key: "trigger", label: "Trigger", kind: "text" },
            { key: "frequency", label: "Frequency", kind: "text" },
            { key: "input_summary", label: "Input", kind: "text" },
            { key: "output_summary", label: "Output", kind: "text" },
            { key: "sla", label: "SLA", kind: "text" },
            { key: "verification_status_code", label: "Verification", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Name", required: true },
            { key: "trigger", label: "Trigger" },
            { key: "frequency", label: "Frequency" },
            { key: "input_summary", label: "Input" },
            { key: "output_summary", label: "Output" },
            { key: "sla", label: "SLA" },
            { key: "exceptions", label: "Exceptions" },
          ]}
        />
      ) : null}

      {tab === "steps" ? (
        <Card title="Workflow steps" flush>
          {steps.loading ? (
            <Loading />
          ) : (steps.data?.items ?? []).length === 0 ? (
            <EmptyState
              title="No steps recorded"
              description="Steps carry the actor, system, input, action, output and the next step."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="numeric">#</th>
                    <th>Actor</th>
                    <th>Input</th>
                    <th>Action</th>
                    <th>Output</th>
                    <th>Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {(steps.data?.items ?? []).map((step) => (
                    <tr key={step.id}>
                      <td className="numeric">{displayValue(step.sequence)}</td>
                      <td>{displayValue(step.actor_label)}</td>
                      <td className="small muted">{displayValue(step.input)}</td>
                      <td>{displayValue(step.action)}</td>
                      <td className="small muted">{displayValue(step.output)}</td>
                      <td>
                        <StatusBadge status={String(step.execution_mode ?? "unknown")} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      {tab === "manual" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="manual-tasks"
          emptyTitle="No manual tasks recorded"
          emptyDescription="Tribal-knowledge and employee interviews populate this automatically."
          columns={[
            { key: "name", label: "Task" },
            { key: "frequency", label: "Frequency", kind: "text" },
            { key: "time_spent_minutes", label: "Minutes" },
            { key: "key_person_dependency", label: "Key person" },
            { key: "is_automatable", label: "Automatable" },
            { key: "verification_status_code", label: "Verification", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Task", required: true },
            { key: "description", label: "Description" },
            { key: "frequency", label: "Frequency" },
            { key: "time_spent_minutes", label: "Minutes per occurrence" },
          ]}
        />
      ) : null}

      {tab === "opportunities" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="automation-opportunities"
          emptyTitle="No automation opportunities yet"
          emptyDescription="Interviews that ask what should be automated populate this list."
          columns={[
            { key: "title", label: "Opportunity" },
            { key: "estimated_impact", label: "Impact", kind: "text" },
            { key: "estimated_effort", label: "Effort", kind: "text" },
            { key: "score", label: "Score" },
            { key: "status", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "title", label: "Title", required: true },
            { key: "description", label: "Description" },
            { key: "estimated_impact", label: "Estimated impact" },
            { key: "estimated_effort", label: "Estimated effort" },
            {
              key: "status",
              label: "Status",
              options: ["identified", "evaluating", "accepted", "rejected", "implemented"],
            },
          ]}
        />
      ) : null}
    </>
  );
}
